/* Partner Reports Center (Phase PP-Reports · Feb 2026).

   Reused across lender/insurance/carrier portals. Banking has its own
   wrapper that uses the same component with a different dataset list.

   Props:
     • portalKey  — 'banking' | 'lender' | 'insurance' | 'carrier'
     • portalName — Display label
     • theme      — colour family
     • datasets   — [{key, label, description, icon, defaultDays}]
*/
import { useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Download, FileText, FileSpreadsheet, FileType2, CalendarRange,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";

const TONE_BG = {
  sky:     "bg-sky-50 border-sky-200 text-sky-700",
  violet:  "bg-violet-50 border-violet-200 text-violet-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  amber:   "bg-amber-50 border-amber-200 text-amber-700",
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 250);
}

function DatasetRow({ portalKey, theme, dataset, sinceDays }) {
  const [busy, setBusy] = useState(null);   // 'csv' | 'pdf' | null
  const tone = TONE_BG[theme] || TONE_BG.sky;
  const Icon = dataset.icon || FileText;

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await api.get(
        `/partner/${portalKey}/reports/${dataset.key}.${fmt}`,
        { params: { since_days: sinceDays }, responseType: "blob" },
      );
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      downloadBlob(res.data, `${portalKey}_${dataset.key}_${today}.${fmt}`);
      toast.success(`Downloaded ${dataset.label} · ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message ||
                  `Could not download ${dataset.label}`);
    } finally { setBusy(null); }
  };

  return (
    <div
      data-testid={`report-row-${dataset.key}`}
      className="flex items-center gap-4 px-4 py-4 border-b border-slate-100 last:border-0"
    >
      <div className={`w-10 h-10 rounded-lg border ${tone} grid place-items-center shrink-0`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900 text-sm">{dataset.label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{dataset.description}</div>
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={!!busy}
        onClick={() => download("csv")}
        data-testid={`report-${dataset.key}-csv`}
      >
        <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />
        {busy === "csv" ? "…" : "CSV"}
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={!!busy}
        onClick={() => download("pdf")}
        data-testid={`report-${dataset.key}-pdf`}
      >
        <FileType2 className="w-3.5 h-3.5 mr-1.5" />
        {busy === "pdf" ? "…" : "PDF"}
      </Button>
    </div>
  );
}

export default function PartnerReports({ portalKey, portalName, theme, datasets }) {
  const [sinceDays, setSinceDays] = useState(30);
  return (
    <div data-testid={`${portalKey}-reports`} className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <Download className="w-6 h-6 text-slate-500" /> Reports
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            One-click CSV or PDF exports of your {portalName.toLowerCase()} datasets.
            PDFs are capped at 5,000 rows · CSVs have no cap.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg border bg-white px-3 py-1.5">
          <CalendarRange className="w-4 h-4 text-slate-400" />
          <span className="text-xs text-slate-500">Window</span>
          <Select value={String(sinceDays)} onValueChange={v => setSinceDays(Number(v))}>
            <SelectTrigger className="h-7 w-28 text-xs" data-testid={`${portalKey}-reports-window`}><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
              <SelectItem value="180">6 months</SelectItem>
              <SelectItem value="365">1 year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="rounded-2xl border border-slate-200 bg-white" data-testid={`${portalKey}-report-list`}>
        {datasets.length === 0 && (
          <div className="text-center text-sm text-slate-500 py-10">
            No reports available for this portal.
          </div>
        )}
        {datasets.map(d => (
          <DatasetRow
            key={d.key}
            portalKey={portalKey}
            theme={theme}
            dataset={d}
            sinceDays={sinceDays}
          />
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600 flex items-start gap-2">
        <Badge variant="outline" className="bg-white">scope</Badge>
        <span>
          Reports require the <code className="bg-white px-1 rounded text-[10px]">partner.{portalKey}.reports.export</code> scope.
          Every download is logged in your verqo partner audit log.
        </span>
      </div>
    </div>
  );
}
